import { useState, useEffect, useRef } from "react";
import { Input } from "@/components";
import { FormCard } from "@/components/FormCard";
import axiosInstance from "@/lib/axios";
import { Copy, CopyCheck, ArrowRight, Phone, Mail, MessageSquare, Link2, Share2, QrCode } from "lucide-react";
import QRCodeStyling from "qr-code-styling";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/utils/cn";

// ─── Step Badge ────────────────────────────────────────────────────────────────
function StepBadge({ n }: { n: number }) {
  return (
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ui-primary/10 text-ui-primary text-xs font-bold border border-ui-primary/25 shrink-0">
      {n}
    </span>
  );
}

// ─── How It Works steps ────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  { icon: Phone, text: "Enter your WhatsApp number with country code" },
  { icon: Mail, text: "Add your business email to track leads" },
  { icon: MessageSquare, text: "Optionally set a pre-filled message for visitors" },
  { icon: Link2, text: "Click generate to create your shareable WhatsApp link" },
  { icon: Share2, text: "Copy the link, share it, or scan the QR code" },
];

const QR_SIZE = 190;

export default function WhatsAppLinkGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const qrCode = useRef<QRCodeStyling | null>(null);
  const qrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: QR_SIZE,
      height: QR_SIZE,
      data: "",
      margin: 8,
      dotsOptions: { color: "#0a0a0a", type: "rounded" },
      backgroundOptions: { color: "#ffffff" },
      cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
      cornersDotOptions: { type: "dot", color: "#000000" },
    });
  }, []);

  useEffect(() => {
    if (!qrRef.current || !qrCode.current) return;
    qrRef.current.innerHTML = "";
    qrCode.current.append(qrRef.current);
    if (generatedLink) {
      qrCode.current.update({ data: generatedLink });
    }
  }, [generatedLink]);


  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const generateLink = () => {
    let isValid = true;
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      isValid = false;
    } else {
      setPhoneError("");
    }
    if (!email.trim()) {
      setEmailError("Business email is required");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Enter a valid business email");
      isValid = false;
    } else {
      setEmailError("");
    }
    if (!isValid) return;

    const formattedPhone = phoneNumber.replace(/[^\d]/g, "");
    let finalMessage = message || "Hello! I'd like to connect with you...";
    const encodedMessage = encodeURIComponent(finalMessage);
    setGeneratedLink(`https://wa.me/${formattedPhone}?text=${encodedMessage}`);

    setIsGenerating(true);
    axiosInstance
      .post("/free-tools/submit", {
        toolType: "whatsapp-link",
        type: 2,
        email: email.trim(),
        phoneNumber: formattedPhone,
        config: {
          message: message,
        },
      })
      .catch((err) => console.error("Failed to capture lead:", err))
      .finally(() => setIsGenerating(false));
  };

  const copyToClipboard = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate = phoneNumber.trim().length > 0 && email.trim().length > 0;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); generateLink(); }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

        {/* ── LEFT COLUMN ── */}
        <div className="space-y-4">

          {/* Step 1 — Enter details */}
          <FormCard title={<span className="flex items-center gap-2"><StepBadge n={1} /> Enter your details</span>}>
            <div className="space-y-4">

              {/* Phone + Email row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* WhatsApp Number */}
                <div className="space-y-1.5">
                  <label htmlFor="link-phone" className="flex items-center gap-1.5 text-xs font-semibold text-ui-body">
                    <Phone size={12} className="text-ui-muted" /> WhatsApp Number
                  </label>
                  <Input
                    id="link-phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      if (e.target.value.trim()) setPhoneError("");
                    }}
                    onKeyDown={(e) => {
                      if (!/[0-9]/.test(e.key) && !["Backspace", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key))
                        e.preventDefault();
                    }}
                    placeholder="1234567890"
                    className="w-full h-9 text-sm"
                  />
                  <p className="text-[10px] text-ui-muted">Include country code, without + symbol</p>
                  {phoneError && <p className="text-[10px] text-ui-error font-medium">{phoneError}</p>}
                </div>

                {/* Business Email */}
                <div className="space-y-1.5">
                  <label htmlFor="link-email" className="flex items-center gap-1.5 text-xs font-semibold text-ui-body">
                    <Mail size={12} className="text-ui-muted" /> Business Email
                  </label>
                  <Input
                    id="link-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmail(v);
                      if (!v.trim()) setEmailError("Business email is required");
                      else if (!validateEmail(v)) setEmailError("Enter a valid business email");
                      else setEmailError("");
                    }}
                    placeholder="business@company.com"
                    className="w-full h-9 text-sm"
                  />
                  {emailError && <p className="text-[10px] text-ui-error font-medium">{emailError}</p>}
                </div>
              </div>

              {/* Pre-filled Message */}
              <div className="space-y-1.5">
                <label htmlFor="link-message" className="flex items-center gap-1.5 text-xs font-semibold text-ui-body">
                  <MessageSquare size={12} className="text-ui-muted" />
                  Pre-filled Message
                  <span className="text-[10px] font-normal text-ui-muted bg-ui-surface-soft px-1.5 py-0.5 rounded-sm border border-ui-hairline">Optional</span>
                </label>
                <textarea
                  id="link-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hello! I'd like to connect with you..."
                  rows={3}
                  className="w-full px-3 py-2 border border-ui-hairline rounded-sm focus:ring-1 focus:ring-ui-primary focus:border-ui-primary bg-ui-surface-card text-sm text-ui-ink resize-none"
                />
                <p className="text-[10px] text-ui-muted">This message will appear pre-typed when someone opens your link.</p>
              </div>

              {/* Generate Button */}
              <button
                type="submit"
                disabled={!canGenerate || isGenerating}
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-2.5 rounded-sm font-semibold text-sm transition-all",
                  (canGenerate && !isGenerating)
                    ? "bg-ui-primary text-white hover:bg-ui-primary-active"
                    : "bg-ui-surface-soft text-ui-muted border border-ui-hairline cursor-not-allowed"
                )}
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <Link2 size={15} />
                    Generate WhatsApp Link
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </FormCard>


          {/* How it works */}
          <FormCard title="How it works">
            <div className="space-y-3">
              {HOW_IT_WORKS.map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-sm bg-ui-primary-light border border-ui-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={10} className="text-ui-primary" />
                  </div>
                  <p className="text-xs text-ui-body leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </FormCard>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="space-y-4 lg:sticky lg:top-0">
          {/* Step 2 — Result: Generated Link */}
          <AnimatePresence>
            {generatedLink && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <FormCard title={<span className="flex items-center gap-2"><StepBadge n={2} /> Your generated link</span>}>
                  <div className="space-y-3">
                    {/* Link copy row */}
                    <div className="flex items-center gap-2 border border-ui-hairline rounded-sm overflow-hidden bg-ui-surface-soft">
                      <span className="flex-1 px-3 text-xs text-ui-body truncate font-mono py-2.5">
                        {generatedLink}
                      </span>
                      <button
                        type="button"
                        onClick={copyToClipboard}
                        className={cn(
                          "flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold transition-all shrink-0",
                          copied ? "bg-ui-success text-white" : "bg-ui-primary text-white hover:bg-ui-primary-active"
                        )}
                      >
                        {copied ? <><CopyCheck size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                      </button>
                    </div>

                    {/* CTA banner */}
                    {/* <div className="flex items-center gap-3 bg-ui-primary/5 border border-ui-primary/15 rounded-sm p-3">
                      <div className="w-8 h-8 bg-ui-primary rounded-full flex items-center justify-center text-white shrink-0">
                        <Zap size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-ui-ink">Automate conversations when someone clicks your link</p>
                        <p className="text-[10px] text-ui-muted leading-tight">TheQuickAssist handles instant auto-replies, lead capture, and full WhatsApp CRM.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.open(generatedLink, "_blank")}
                        className="flex items-center gap-1 bg-ui-primary text-white text-[10px] font-bold px-3 py-1.5 rounded-sm whitespace-nowrap hover:bg-ui-primary-active transition-colors shrink-0"
                      >
                        Start free <ArrowRight size={10} />
                      </button>
                    </div> */}
                  </div>
                </FormCard>
              </motion.div>
            )}
          </AnimatePresence>
          {/* QR Code Preview Panel */}
          <FormCard
            title={
              <span className="flex items-center gap-2">
                {generatedLink ? <StepBadge n={3} /> : null}
                QR Code Preview
              </span>
            }
            action={
              generatedLink ? (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-ui-error" />
                  <span className="w-2 h-2 rounded-full bg-ui-warning" />
                  <span className="w-2 h-2 rounded-full bg-ui-success" />
                  <span className="text-[10px] font-bold text-ui-muted ml-1 uppercase tracking-wider">LIVE</span>
                </div>
              ) : undefined
            }
          >
            <div className="flex flex-col items-center justify-center min-h-[200px]">
              {generatedLink ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="flex flex-col items-center gap-3"
                >
                  {/* QR Card frame */}
                  <div className="flex flex-col items-center bg-white border-2 border-ui-ink rounded-2xl shadow-md p-3">
                    <div className="px-5 py-1.5 bg-ui-primary text-white text-[10px] font-bold rounded-full uppercase tracking-widest mb-1">
                      SCAN ME
                    </div>
                    {/* Exact pixel wrapper to prevent SVG stretching */}
                    <div
                      style={{ width: QR_SIZE, height: QR_SIZE, flexShrink: 0 }}
                      className="bg-white rounded overflow-hidden"
                    >
                      <div ref={qrRef} style={{ width: QR_SIZE, height: QR_SIZE, display: "block" }} />
                    </div>
                  </div>
                  <p className="text-[10px] text-ui-muted text-center">Scan to open WhatsApp with your pre-filled message</p>
                </motion.div>
              ) : (
                <div className="flex flex-col items-center gap-3 text-center px-6">
                  <div className="w-16 h-16 rounded-xl bg-ui-primary-light border border-ui-primary/20 flex items-center justify-center">
                    <QrCode size={28} className="text-ui-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-ui-ink">QR Code appears here</p>
                    <p className="text-xs text-ui-muted mt-1 leading-relaxed">
                      Fill in your WhatsApp number and email, then click<br />"Generate" to create your link and QR code.
                    </p>
                  </div>
                  {/* Feature highlights */}
                  <div className="w-full mt-1 grid grid-cols-1 gap-2">
                    {[
                      { icon: Link2, label: "Shareable WhatsApp link" },
                      { icon: QrCode, label: "Downloadable QR code" },
                      { icon: MessageSquare, label: "Pre-filled message support" },
                    ].map(({ icon: Icon, label }) => (
                      <div key={label} className="flex items-center gap-2 bg-ui-surface-soft border border-ui-hairline rounded-sm px-3 py-2">
                        <Icon size={13} className="text-ui-primary shrink-0" />
                        <span className="text-[11px] text-ui-body font-medium">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>



          </FormCard>

          {/* Link info card (visible in empty state) */}
          {!generatedLink && (
            <FormCard title="About WhatsApp Link Generator">
              <div className="space-y-2.5">
                <p className="text-xs text-ui-body leading-relaxed">
                  Create a direct WhatsApp link that opens a conversation with your number — with a pre-filled message already typed in for the visitor.
                </p>
                <div className="rounded-sm bg-ui-primary-light border border-ui-primary/15 p-3">
                  <p className="text-[11px] text-ui-primary font-semibold leading-relaxed">
                    💡 Great for: marketing campaigns, landing pages, email signatures, and business cards.
                  </p>
                </div>
              </div>
            </FormCard>
          )}


          

        </div>
      </div>
    </form>
  );
}
